import isString from "lodash/isString";
import { FormAssignDto } from "../../model/form/dto/AssignFormDto";
import { CloneFormDto } from "../../model/form/dto/CloneFormDto";
import { ConfigureFormDto } from "../../model/form/dto/ConfigureFormDto";
import { CreateFormDto } from "../../model/form/dto/CreateFormDto";
import { DeleteFormDto } from "../../model/form/dto/DeleteFormDto";
import { GetUserFormsDto } from "../../model/form/dto/GetUserFormsDto";
import { UpdateFormDto } from "../../model/form/dto/UpdateFormDto";
import { AtiraApi } from "../AxiosClient";
import { CreateFormsFolderDto } from "../../model/form/dto/CreateFormsFolderDto";
import { UpdateFormsFolderDto } from "../../model/form/dto/UpdateFormsFolderDto";
import { DeleteFormsFolderDto } from "../../model/form/dto/DeleteFormsFolderDto";
import { GetFormFoldersDto } from "../../model/form/dto/GetFormFoldersDto";
import { FormsFolder } from "../../model/form/types/FormsFolder";
import { AddRemoveFormFromToFolderDto } from "../../model/form/dto/AddRemoveFormFromToFolderDto";
import { Form } from "../../model/form/Form";

export class FormAxios {
  private static appendFormImages(
    img: File | string,
    formData: FormData,
    fieldName: string,
    index: number,
  ) {
    if (img instanceof File || img === null) {
      formData.append(`${fieldName}${index + 1}`, img);
    }
  }

  static async getForms(dto: GetUserFormsDto) {
    return AtiraApi.post(`/form/user/${dto.userId}`, dto).then(
      ({ data }) => data,
    );
  }

  static async createForm(dto: CreateFormDto) {
    const formData = new FormData();
    const { style, ...payload } = dto;

    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, isString(value) ? value : JSON.stringify(value));
    });

    const {
      header: { images: headerImages = [], ...headerValues } = {},
      leftSide: {
        images: leftSideImages = [],
        content: leftSideContent,
        header: leftSideHeader = {},
      } = {},
      footer: { content: footerContent } = {},
      ...rest
    } = style || {};

    headerImages?.forEach((img, index) =>
      FormAxios.appendFormImages(img, formData, "imgHeaderImage", index),
    );
    leftSideImages?.forEach((img, index) =>
      FormAxios.appendFormImages(img, formData, "imgLeftSideImage", index),
    );

    const remainingStyle = {
      header: { ...headerValues },
      leftSide: {
        content: leftSideContent || [],
        header: leftSideHeader || {},
      },
      footer: { content: footerContent || [] },
      ...rest,
    };
    formData.append("style", JSON.stringify(remainingStyle));
    return AtiraApi.post(`/form/create`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then(({ data }) => data);
  }

  static async deleteForm(dto: DeleteFormDto) {
    return AtiraApi.post(`/form/${dto.formId}/delete`, dto).then(
      ({ data }) => data,
    );
  }

  static updateForm(dto: UpdateFormDto) {
    const formData = new FormData();
    const { style, ...payload } = dto;

    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, isString(value) ? value : JSON.stringify(value));
    });

    const {
      header: { images: headerImages = [], ...headerValues } = {},
      leftSide: {
        images: leftSideImages = [],
        header: leftSideHeader = {},
        content: leftSideContent,
      } = {},
      footer: { content: footerContent } = {},
      ...rest
    } = style || {};

    headerImages?.forEach((img: any, index) =>
      FormAxios.appendFormImages(img, formData, "imgHeaderImage", index),
    );

    leftSideImages?.forEach((img: any, index) =>
      FormAxios.appendFormImages(img, formData, "imgLeftSideImage", index),
    );

    const remainingStyle = {
      header: { ...headerValues },
      leftSide: {
        content: leftSideContent || [],
        header: leftSideHeader || {},
      },
      footer: { content: footerContent || [] },
      ...rest,
    };
    formData.append("style", JSON.stringify(remainingStyle));

    return AtiraApi.patch<void>(`/form/${dto.formId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then(({ data }) => data);
  }

  static cloneForm(dto: CloneFormDto) {
    return AtiraApi.post<Form>(`/form/${dto.formId}/clone`, dto).then(
      ({ data }) => data,
    );
  }

  static assignForm(dto: FormAssignDto) {
    return AtiraApi.post<void>(
      `/form/${dto.formId}/manage-assignees`,
      dto,
    ).then(({ data }) => data);
  }

  static configureForm(dto: ConfigureFormDto) {
    return AtiraApi.patch<void>(`/form/${dto.formId}/config`, dto).then(
      ({ data }) => data,
    );
  }

  static createFolder(dto: CreateFormsFolderDto) {
    return AtiraApi.post<void>(`/form/folder/create`, dto).then(
      ({ data }) => data,
    );
  }

  static getFolders(dto: GetFormFoldersDto) {
    return AtiraApi.post<FormsFolder[]>("/form/folder", dto).then(
      ({ data }) => data,
    );
  }

  static updateFolder(dto: UpdateFormsFolderDto) {
    return AtiraApi.patch<void>(`/form/folder/${dto.formFolderId}`, dto).then(
      ({ data }) => data,
    );
  }

  static deleteFolder(dto: DeleteFormsFolderDto) {
    return AtiraApi.delete(`/form/folder/${dto.formFolderId}`, {
      data: dto,
    }).then(({ data }) => data);
  }

  static addRemoveFormToFolder(dto: AddRemoveFormFromToFolderDto) {
    return AtiraApi.post<void>(
      `/form/${dto.formId}/manage-folder/${dto.formFolderId}`,
      dto,
    ).then(({ data }) => data);
  }
}
